import type {ChangeSpec} from '@codemirror/state'
import type {EditorView} from '@codemirror/view'
import type {Heading, Root} from 'mdast'

import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {containsRange, getNodesOverlappingRange} from '../../../language-tools/syntax-tree/node-range-utils'
import {isPositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6
export type Direction = 'increase' | 'decrease'

/**
 * Finds the first heading node that overlaps with the given range.
 */
const findHeadingNode = (tree: Root, start: number, end: number) =>
  getNodesOverlappingRange(tree, start, end).find(node => node.type === 'heading')

/**
 * Gets the heading nodes for all lines in the current selection.
 * Returns an array where each element corresponds to a selected line and contains
 * the heading node for that line, or undefined if the line is not a heading.
 * @returns An array of heading nodes (or undefined) for each selected line, or null if no headings are found
 */
const getHeadingNodes = (view: EditorView): Array<Heading | undefined> | null => {
  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()
  const {from, to} = view.state.selection.main
  const selectedLines = MarkdownLine.linesAtRange(tree, source, from, to)
  const headingsByLine = selectedLines.map(line => findHeadingNode(tree, line.start, line.end))

  const hasHeading = headingsByLine.some(n => n && n.type === 'heading')
  if (!hasHeading) return null

  return headingsByLine
}

/**
 * Returns the heading level if all selected lines have the same heading level.
 * Returns undefined if not all lines are headings or if they have different levels.
 */
export const getHeadingLevelIfUniform = (view: EditorView): HeadingLevel | undefined => {
  const selectedHeadings = getHeadingNodes(view)
  const headingNodes = selectedHeadings?.filter((n): n is Heading => !!n && n.type === 'heading') ?? []

  // Check if all selected lines are heading nodes
  if (headingNodes.length === 0 || headingNodes.length !== selectedHeadings?.length) return undefined

  const firstDepth = headingNodes[0]!.depth
  const allSameLevel = headingNodes.every(node => node.depth === firstDepth)
  return allSameLevel ? firstDepth : undefined
}

/**
 * Checks whether the heading level can be adjusted in the given direction.
 * Returns false if all selected headings are already at the maximum (6) or minimum (1) level for the given direction.
 * @returns True if the heading level can be adjusted, false otherwise
 */
export const canAdjustHeadingLevel = (view: EditorView, direction: Direction): boolean => {
  const selectedHeadings = getHeadingNodes(view)
  if (!selectedHeadings) return false

  // Disable increase if all headings are at level 6
  if (direction === 'increase' && selectedHeadings.every(n => !n || n.depth === 6)) return false

  // Disable decrease if all headings are at level 1
  if (direction === 'decrease' && selectedHeadings.every(n => !n || n.depth === 1)) return false

  return true
}

/**
 * Formats the selected lines as a heading at the specified level.
 * If all selected lines are already at the target level, removes the heading formatting.
 * Supports both ATX (# prefix) and Setext (underline) heading styles.
 * @param level - The heading level to apply (1-6)
 */
export const formatHeading = (view: EditorView, level: HeadingLevel) => {
  if (!view.hasFocus) view.focus()

  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()
  const selection = view.state.selection.main
  const selectedLines = MarkdownLine.linesAtRange(tree, source, selection.from, selection.to)
  const atxPrefix = `${'#'.repeat(level)} `

  // Check if all selected lines are already at the target heading level
  const headingNodes = selectedLines.map(line => findHeadingNode(tree, line.start, line.end))
  const shouldAddLevel = !(headingNodes.length > 0 && headingNodes.every(node => node?.depth === level))

  const changes: ChangeSpec[] = []
  let changeSelection = undefined

  for (const line of selectedLines) {
    const headingNode = findHeadingNode(tree, line.start, line.end)
    let headingDelimiter = null

    // Remove existing heading formatting
    if (headingNode) {
      for (const child of headingNode.children ?? []) {
        if (!isPositionedNode(child) || child.type !== 'delimiter') continue

        const isATXDelimiter = child.tokenType === 'atxHeadingSequence'
        const isSetextDelimiter = child.tokenType === 'setextHeadingLine'
        if (!isATXDelimiter && !isSetextDelimiter) continue

        headingDelimiter = child
        if (isATXDelimiter) {
          // Remove ATX heading prefix (include trailing whitespace)
          changes.push({
            from: child.position.start.offset,
            to: child.position.end.offset + 1,
          })
        } else {
          // Remove setext heading - remove the entire underline
          const setextLine = line.next()
          if (setextLine) {
            changes.push({
              from: setextLine.start,
              to: setextLine.end,
            })
          }
        }
        break
      }
    }

    // Add new heading formatting if needed
    if (shouldAddLevel) {
      changes.push({
        from: line.start,
        insert: atxPrefix,
      })

      const withinHeadingDelimiter =
        headingDelimiter &&
        isPositionedNode(headingDelimiter) &&
        containsRange(
          selection.from,
          selection.to,
          headingDelimiter.position.start.offset,
          headingDelimiter.position.end.offset,
          true,
        )
      // Handle caret position when selection is at line start or within heading delimiter
      if (selection.empty && (selection.from === line.start || withinHeadingDelimiter)) {
        changeSelection = {anchor: line.start + atxPrefix.length}
      }
    }
  }

  view.dispatch({
    changes,
    selection: changeSelection,
    userEvent: 'input',
    scrollIntoView: true,
  })

  return true
}

/**
 * Adjusts the heading level of all selected lines in the given direction.
 * Increases heading level (# to ##) or decreases it (## to #).
 * Only works when all selected lines are headings and within valid level bounds.
 * @returns True if the adjustment was successful, false otherwise
 */
export const adjustHeadingLevel = (view: EditorView | null, direction: Direction): boolean => {
  if (!view) return false

  // Check if adjustment is possible
  if (!canAdjustHeadingLevel(view, direction)) return false

  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()
  const {from, to} = view.state.selection.main
  const selectedLines = MarkdownLine.linesAtRange(tree, source, from, to)

  const changes: ChangeSpec[] = []

  for (const line of selectedLines) {
    const headingNode = findHeadingNode(tree, line.start, line.end)
    if (!headingNode) continue

    let newLevel: HeadingLevel
    if (direction === 'increase') {
      newLevel = Math.min(6, headingNode.depth + 1) as HeadingLevel
    } else {
      newLevel = Math.max(1, headingNode.depth - 1) as HeadingLevel
    }

    // Find and update the heading delimiter
    for (const child of headingNode.children ?? []) {
      if (!isPositionedNode(child) || child.type !== 'delimiter') continue

      const isATXDelimiter = child.tokenType === 'atxHeadingSequence'
      const isSetextDelimiter = child.tokenType === 'setextHeadingLine'
      if (!isATXDelimiter && !isSetextDelimiter) continue

      if (isATXDelimiter) {
        // Replace existing ATX prefix
        changes.push({
          from: child.position.start.offset,
          to: child.position.end.offset,
          insert: '#'.repeat(newLevel),
        })
      } else {
        // Convert setext to ATX with increased level
        const setextLine = line.next()
        if (setextLine) {
          // Remove the setext underline
          changes.push({
            from: setextLine.start,
            to: setextLine.end,
          })
          // Add ATX prefix at line start
          changes.push({
            from: line.start,
            insert: `${'#'.repeat(newLevel)} `,
          })
        }
      }
      break
    }
  }

  if (changes.length === 0) return false

  view.dispatch({
    changes,
    userEvent: 'input',
    scrollIntoView: true,
  })

  return true
}
